import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { getLocationLandingPath } from './pages/locationSeoData';

export const TRENDING_MOVING_KEYWORDS = [
  'moving company',
  'moving truck rental',
  'moving services',
  'movers near me',
  'mover near me',
  'moving boxes',
  'moving companies near me',
];

export const TRENDING_CLEANING_KEYWORDS = [
  'cleaning services near me',
  'house cleaning services',
  'apartment cleaning services',
];

export const TRENDING_KEYWORD_STRING = [
  ...TRENDING_MOVING_KEYWORDS,
  ...TRENDING_CLEANING_KEYWORDS,
].join(', ');

const formatKeywordList = (keywords) =>
  keywords.map((keyword, index) => (
    <React.Fragment key={keyword}>
      <span className="font-semibold text-gray-900">{keyword}</span>
      {index === keywords.length - 1 ? '' : index === keywords.length - 2 ? ', and ' : ', '}
    </React.Fragment>
  ));

export const HomepageSeoContentSection = () => {
  const featuredCities = [
    { slug: 'columbus', label: 'Columbus' },
    { slug: 'indianapolis', label: 'Indianapolis' },
    { slug: 'bloomington', label: 'Bloomington' },
    { slug: 'carmel', label: 'Carmel' },
    { slug: 'fishers', label: 'Fishers' },
    { slug: 'greenwood', label: 'Greenwood' },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-sky-100 text-sky-blue hover:bg-sky-100">
              Search-Driven Service Content
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-4">
              Indiana Moving Company and Cleaning Services That Match What Customers Search For
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Swift Move and Clean is built for the real searches people make when they need help fast:
              {' '}<span className="font-semibold text-gray-900">moving company</span>,
              {' '}<span className="font-semibold text-gray-900">moving truck rental</span>,
              {' '}<span className="font-semibold text-gray-900">moving services</span>,
              {' '}<span className="font-semibold text-gray-900">movers near me</span>,
              {' '}<span className="font-semibold text-gray-900">mover near me</span>,
              {' '}<span className="font-semibold text-gray-900">moving boxes</span>,
              {' '}<span className="font-semibold text-gray-900">moving companies near me</span>,
              {' '}<span className="font-semibold text-gray-900">cleaning services near me</span>,
              {' '}<span className="font-semibold text-gray-900">house cleaning services</span>, and
              {' '}<span className="font-semibold text-gray-900">apartment cleaning services</span>.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <Card className="border-0 shadow-md">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Moving Services Across Indiana
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you are comparing a <strong>moving company</strong>, shopping for
                  {' '}<strong>moving boxes</strong>, checking options for
                  {' '}<strong>moving truck rental</strong>, or narrowing down the best
                  {' '}<strong>moving companies near me</strong> for your home or business, our team is set up to help.
                  We handle apartment moves, family relocations, office transitions, packing support,
                  and long-distance routes from Columbus to the Indianapolis metro and other Indiana communities with full
                  {' '}<strong>moving services</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We keep pricing transparent, protect furniture and floors, and give customers a fast path
                  from quote to schedule. That is why homeowners, renters, and businesses across Indiana turn
                  to Swift Move and Clean when they need dependable <strong>movers near me</strong> or even start with a simple
                  {' '}<strong>mover near me</strong> search on their phone.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cleaning Services Customers Already Search For
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We also speak to the cleaning searches customers use every day, including
                  {' '}<strong>cleaning services near me</strong>,
                  {' '}<strong>house cleaning services</strong>, and
                  {' '}<strong>apartment cleaning services</strong>.
                  Our core focus is recurring residential cleaning, deep cleaning, move-in and move-out cleaning,
                  and office cleaning in the areas we actively cover.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  On move-related jobs, we help customers combine practical cleaning with moving support so they
                  can hand over a cleaner property, settle into a fresher home, and reduce the number of separate
                  appointments they need to manage.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-sky-50 to-orange-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Featured Indiana Cities
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We serve customers in{' '}
              {featuredCities.map((city, index) => (
                <React.Fragment key={city.slug}>
                  <Link
                    to={getLocationLandingPath(city.slug)}
                    className="font-semibold text-sky-blue hover:underline"
                  >
                    {city.label}
                  </Link>
                  {index === featuredCities.length - 1 ? '' : index === featuredCities.length - 2 ? ', and ' : ', '}
                </React.Fragment>
              ))}
              {' '}plus dozens of surrounding communities across Indiana.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Whether you started by searching for a <strong>moving company</strong> or for
              {' '}<strong>cleaning services near me</strong>, our goal is the same:
              reliable crews, clear communication, and a smoother start in your next space.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const CitySeoContentSection = ({ loc, isFullService }) => {
  const nearbyNeighborhoods = (loc.neighborhoods || []).slice(0, 3).join(', ');
  const servedZips = (loc.zipCodes || []).slice(0, 3).join(', ');

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-orange-100 text-bright-orange hover:bg-orange-100">
              Full HTML City Content
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-4">
              Moving and Cleaning Content for {loc.name}, {loc.stateAbbr}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              People in {loc.name} often start with the same high-intent searches:
              {' '}{formatKeywordList(TRENDING_MOVING_KEYWORDS)} for moving and
              {' '}{formatKeywordList(TRENDING_CLEANING_KEYWORDS)} for cleaning.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <Card className="border-0 shadow-md lg:col-span-2">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Your Local {loc.name} Moving Company
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When families and businesses in {loc.name} search for a trusted
                  {' '}<strong>moving company</strong>, they usually want three things:
                  careful crews, clear pricing, and a team that knows the local streets, parking patterns,
                  and neighborhoods. Swift Move and Clean serves {servedZips || `${loc.name} area zip codes`}
                  {' '}with practical moving support for apartments, houses, offices, and specialty items.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Customers who compare <strong>moving companies near me</strong> often tell us the biggest
                  difference is preparation. We show up with the right truck plan, padding, and scheduling details
                  so your move in {loc.name} feels organized instead of rushed. If you are still comparing
                  {' '}<strong>moving truck rental</strong> options or collecting
                  {' '}<strong>moving boxes</strong>, we can help you plan packing, loading, and timing
                  around your closing date, lease turnover, or office handoff with full
                  {' '}<strong>moving services</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  From neighborhoods like {nearbyNeighborhoods || loc.name} to the surrounding routes that connect
                  {` ${loc.name} `}to other Indiana cities, our crews focus on damage prevention, efficient loading,
                  and a smoother moving day from the first call to the final placement of furniture for anyone searching
                  {' '}<strong>movers near me</strong> or a fast local <strong>mover near me</strong>.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Search Terms We Address
                </h3>
                <ul className="space-y-3 text-gray-700">
                  {TRENDING_MOVING_KEYWORDS.map((keyword) => (
                    <li key={keyword}>
                      <strong>{keyword}</strong>
                    </li>
                  ))}
                  {TRENDING_CLEANING_KEYWORDS.map((keyword) => (
                    <li key={keyword}>
                      <strong>{keyword}</strong>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-md">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cleaning Services in {loc.name}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Customers planning a move also search for <strong>cleaning services near me</strong> because
                  they need the property ready for a buyer, landlord, tenant, or new staff team. In the areas where
                  we actively schedule cleaning, Swift Move and Clean handles
                  {' '}<strong>house cleaning services</strong>, move-in and move-out cleaning, and
                  {' '}<strong>apartment cleaning services</strong> with the same detail-oriented mindset we bring to every move.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We also understand that customers often compare a few cleaning options before booking, especially when they
                  need one team to coordinate timing around keys, elevators, leasing offices, or final walkthroughs.
                  {isFullService
                    ? ` In ${loc.name}, we can often bundle move-related cleaning into one coordinated schedule so your handoff is cleaner and easier.`
                    : ` In ${loc.name}, our primary focus is moving service, but we still help customers think through the right next step for move-related cleaning needs and available bundled options.`}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Why This Page Matters for {loc.name}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This page is designed to answer the practical questions people have before booking:
                  who is the right <strong>moving company</strong>, what local zip codes are covered,
                  whether <strong>moving boxes</strong> and packing help are available, how
                  {' '}<strong>moving services</strong> compare with a basic <strong>moving truck rental</strong> plan,
                  and where to find dependable <strong>cleaning services near me</strong> without wasting time.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  If you are in {loc.name}, {loc.stateAbbr}, and looking for dependable help,
                  Swift Move and Clean gives you one place to review service details, nearby coverage,
                  FAQs, and next steps for a free quote.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
